// Fork: choose which status page components an incident affects.
import { useEffect, useState } from 'react'
import {
  getIncidentStatusComponents,
  listStatusComponents,
  setIncidentStatusComponents,
  type StatusComponent,
} from '../../api/statusComponents'

interface IncidentStatusComponentsProps {
  incidentId: string
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export function IncidentStatusComponents({ incidentId, onSuccess, onError }: IncidentStatusComponentsProps) {
  const [components, setComponents] = useState<StatusComponent[]>([])
  const [linked, setLinked] = useState<string[]>([])
  const [matched, setMatched] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([listStatusComponents(), getIncidentStatusComponents(incidentId)])
      .then(([list, links]) => {
        if (cancelled) return
        setComponents(list.components)
        setLinked(links.component_ids)
        setMatched(links.matched_component_ids)
      })
      .catch(() => { /* components are optional; hide the picker */ })
    return () => { cancelled = true }
  }, [incidentId])

  if (components.length === 0) return null

  async function toggle(id: string, checked: boolean) {
    const next = checked ? [...linked, id] : linked.filter(existing => existing !== id)
    setSaving(true)
    try {
      await setIncidentStatusComponents(incidentId, next)
      setLinked(next)
      onSuccess('Affected components updated')
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to update affected components')
    } finally {
      setSaving(false)
    }
  }

  return (
    <fieldset className="w-full mt-2" disabled={saving}>
      <legend className="text-sm font-medium text-text-primary">Affected components</legend>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        {components.map(component => {
          const viaLabel = matched.includes(component.id)
          return (
            <label key={component.id} className="inline-flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={viaLabel || linked.includes(component.id)}
                disabled={viaLabel}
                onChange={event => toggle(component.id, event.target.checked)}
                className="rounded border-border focus:ring-brand-primary disabled:opacity-50"
              />
              {component.name}
              {viaLabel && <span className="text-xs text-text-secondary">(from alert label)</span>}
            </label>
          )
        })}
      </div>
      <p className="mt-1 text-xs text-text-secondary">
        Counts toward the component’s uptime on the status page once the incident is published.
      </p>
    </fieldset>
  )
}
